import { API_ENV_URLS } from "../../constants/api";
import { apiFetch } from "../../services/apiClient";

const baseUrl = API_ENV_URLS.sarvam;

function url(path, params) {
  if (!baseUrl) throw new Error("Sarvam API base URL is not configured.");
  const result = new URL(`${baseUrl.replace(/\/+$/, "")}${path}`);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      result.searchParams.set(key, value);
  });
  return result.toString();
}

async function request(path, options = {}, params) {
  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (options.body !== undefined)
    headers.set("Content-Type", "application/json");
  // A deployed server-side proxy should inject x-api-key. This optional value is isolated here
  // for installations that explicitly provide it through runtime configuration.
  const apiKey = process.env.REACT_APP_SARVAM_API_KEY;
  if (apiKey) headers.set("x-api-key", apiKey);
  // Sarvam authenticates these operations with x-api-key. Do not attach the
  // intranet Bearer token or invoke its refresh flow for this API.
  const response = await apiFetch(url(path, params), { ...options, headers, skipAuth: true });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    /* empty response */
  }
  if (!response.ok)
    throw new Error(
      payload?.message ||
        payload?.error ||
        `Sarvam request failed (${response.status}).`,
    );
  return payload?.data ?? payload;
}

const get = (path, params) => request(path, {}, params);
const post = (path, body) =>
  request(path, { method: "POST", body: JSON.stringify(body) });

export const sarvamApi = {
  dashboard: (businessDate) =>
    get("/api/operations/dashboard", { businessDate }),
  plans: () => get("/api/automation-plans"),
  plan: (id) => get(`/api/automation-plans/${encodeURIComponent(id)}`),
  audit: (id) =>
    get(`/api/automation-plans/${encodeURIComponent(id)}/audit`, { limit: 25 }),
  validate: (id) =>
    post(`/api/automation-plans/${encodeURIComponent(id)}/validate`),
  dryRun: (id) =>
    post(`/api/automation-plans/${encodeURIComponent(id)}/dry-run`),
  enable: (id, body) =>
    post(`/api/automation-plans/${encodeURIComponent(id)}/enable`, body),
  disable: (id, body) =>
    post(`/api/automation-plans/${encodeURIComponent(id)}/disable`, body),
  runs: (params) => get("/api/automation-runs", params),
  run: (id) => get(`/api/automation-runs/${encodeURIComponent(id)}`),
  activity: (id) =>
    get(`/api/automation-runs/${encodeURIComponent(id)}/activity`),
  stageRun: (id, body) =>
    post(`/api/automation-plans/${encodeURIComponent(id)}/runs`, body),
  resume: (id) =>
    post(`/api/automation-runs/${encodeURIComponent(id)}/resume`, {
      confirm: true,
      stageOnly: true,
    }),
  provision: (id) =>
    post(`/api/automation-runs/${encodeURIComponent(id)}/provision`, {
      confirm: true,
      stageOnly: false,
    }),
  campaigns: (params) => get("/api/campaigns", params),
  campaignStatus: (id) =>
    get(`/api/campaigns/${encodeURIComponent(id)}/status`),
  retryCohort: (id) =>
    post(`/api/campaigns/${encodeURIComponent(id)}/cohort/retry`),
  syncResults: (id) =>
    post(`/api/campaigns/${encodeURIComponent(id)}/results/sync`),
  deadLetters: (params) => get("/api/result-dead-letters", params),
  retryDeadLetter: (id) =>
    post(`/api/result-dead-letters/${encodeURIComponent(id)}/retry`),
  legacyDryRun: (body) => post("/api/campaign-batches/dry-run", body),
  legacyCreate: (body) => post("/api/campaign-batches", body),
  legacyStatus: (id) =>
    get(`/api/campaign-batches/${encodeURIComponent(id)}/status`),
  legacyReplacements: (id, body) =>
    post(`/api/campaign-batches/${encodeURIComponent(id)}/replacements`, body),
};
